import React from 'react'
import { KpiCard } from './ui'
import type { RiskLevel } from '../lib/types'

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'Healthy',
  moderate: 'Moderate Risk',
  high: 'Needs Attention',
  critical: 'Critical',
}

const RISK_COLORS: Record<RiskLevel, string> = {
  low: 'badge-green',
  moderate: 'badge-amber',
  high: 'badge-orange',
  critical: 'badge-red',
}

/** Human-first status display for Organization Health, Provider Risk, and
 * Patient Journey. The qualitative label is always the primary visual
 * element; the numeric score and its full component breakdown are shown as
 * supporting detail underneath — never hidden, never primary. In `compact`
 * mode (list rows) only the label + score render; the full breakdown is
 * reserved for detail pages so a score is never shown with zero way to see
 * what produced it. */
export function StatusBanner({
  riskLevel, score, components, compact, journeyLabel,
}: {
  riskLevel: RiskLevel
  score?: number
  components?: Array<{ label: string; value: React.ReactNode }>
  compact?: boolean
  /** For Patient Journey, which isn't a risk score — a plain-language status instead. */
  journeyLabel?: string
}) {
  const label = journeyLabel || RISK_LABELS[riskLevel]
  const colorClass = journeyLabel ? 'badge-blue' : RISK_COLORS[riskLevel]

  return (
    <div className={`status-banner ${compact ? 'status-banner-compact' : ''}`}>
      <div className="status-banner-head">
        <span className={`status-banner-label badge ${colorClass}`}>{label}</span>
        {score != null && <span className="status-banner-score muted">Score: {score}/100</span>}
      </div>
      {!compact && components && components.length > 0 && (
        <div className="kpis" style={{ marginTop: 10 }}>
          {components.map((c) => (
            <KpiCard key={c.label} label={c.label} value={c.value} />
          ))}
        </div>
      )}
    </div>
  )
}
